import { BaseActivity } from "../../model/activity/BaseActivity";
import { Kanban } from "../../model/kanban/Kanban";
import { KanbanAutomationRule } from "../../model/kanban/KanbanAutomationRule";
import { KanbanCard } from "../../model/kanban/KanbanCard";
import { KanbanColumn } from "../../model/kanban/KanbanColumn";
import { CreateKanbanColumnDto } from "../../model/kanban/dto/CreateKanabnColumnDto";
import { CreateKanbanAutomationRuleDto } from "../../model/kanban/dto/CreateKanbanAutomationRuleDto";
import { CreateKanbanCardDto } from "../../model/kanban/dto/CreateKanbanCardDto";
import { CreateKanbanDto } from "../../model/kanban/dto/CreateKanbanDto";
import { DeleteKanbanAutomationRuleDto } from "../../model/kanban/dto/DeleteKanbanAutomationRuleDto";
import { DeleteKanbanCardDto } from "../../model/kanban/dto/DeleteKanbanCardDto";
import { DeleteKanbanDto } from "../../model/kanban/dto/DeleteKanbanDto";
import { ExportKanbanCardsDto } from "../../model/kanban/dto/ExportKanbanCardsDto";
import { GetKanbanActivitiesDto } from "../../model/kanban/dto/GetKanbanActivitiesDto";
import { GetKanbanCardByIdDto } from "../../model/kanban/dto/GetKanbanCardByIdDto";
import { GetKanbanCardsByKanbanColumnIdDto } from "../../model/kanban/dto/GetKanbanCardsByKanbanColumnIdDto";
import { GetUserKanbanDto } from "../../model/kanban/dto/GetUserDefaultKanbanDto";
import { GetUserKanbansMetaDto } from "../../model/kanban/dto/GetUserDefaultKanbansMetaDto";
import { GetUserKanbansMetaResponseDto } from "../../model/kanban/dto/GetUserDefaultKanbansMetaResponseDto";
import { UpdateKanbanAutomationRuleDto } from "../../model/kanban/dto/UpdateKanbanAutomationRuleDto";
import { UpdateKanbanCardBetweenKanbansDto } from "../../model/kanban/dto/UpdateKanbanCardBetweenKanbansDto";
import { UpdateKanbanCardDto } from "../../model/kanban/dto/UpdateKanbanCardDto";
import { UpdateKanbanColumnDto } from "../../model/kanban/dto/UpdateKanbanColumnDto";
import { UpdateKanbanDto } from "../../model/kanban/dto/UpdateKanbanDto";
import { UpdateKanbanClosingReasonsDto } from "../../model/kanban/dto/UpdateKanbanClosingReasonsDto";
import { PageResponseDto } from "../../model/meta/PageResponseDto";
import { AssignTaskDto } from "../../model/shared/dto/AssignTaskDto";
import { UnAssignTaskDto } from "../../model/shared/dto/UnAssignTaskDto";
import { AtiraApi } from "../AxiosClient";

export class KanbanAxios {
  static createKanban(dto: CreateKanbanDto) {
    return AtiraApi.post<void>("/kanban/create", dto).then(({ data }) => data);
  }

  static getUserKanban(dto: GetUserKanbanDto) {
    return AtiraApi.post<Kanban>(
      `/kanban/${dto.kanbanId || "default"}`,
      dto,
    ).then(({ data }) => data);
  }

  static getUserKanbansMeta(dto: GetUserKanbansMetaDto) {
    return AtiraApi.post<GetUserKanbansMetaResponseDto>(
      `/kanban/${dto.userId}/meta`,
      dto,
    ).then(({ data }) => data);
  }

  static getKanbanCardsByKanbanColumnId(
    dto: GetKanbanCardsByKanbanColumnIdDto,
  ) {
    return AtiraApi.post<PageResponseDto<KanbanCard[]>>(
      `/kanban/column/${dto.columnId}`,
      dto,
    ).then(({ data }) => data);
  }

  static createKanbanCard(dto: CreateKanbanCardDto) {
    return AtiraApi.post<void>(`/kanban/card`, dto).then(({ data }) => data);
  }

  static getKanbanCardById(dto: GetKanbanCardByIdDto) {
    return AtiraApi.post<KanbanCard>(`/kanban/card/${dto.cardId}`, dto).then(
      ({ data }) => data,
    );
  }

  // A move returns the whole board (sibling card orders shifted); a pure
  // field edit returns just the updated card for the reducer to patch in.
  static updateKanbanCard(dto: UpdateKanbanCardDto) {
    return AtiraApi.patch<Kanban | KanbanCard>(
      `/kanban/card/${dto.cardId}`,
      dto,
    ).then(({ data }) => data);
  }

  // A reorder returns the whole board; a rename returns just the column.
  static updateKanbanColumnById(dto: UpdateKanbanColumnDto) {
    return AtiraApi.patch<Kanban | KanbanColumn>(
      `/kanban/column/${dto.columnId}`,
      dto,
    ).then(({ data }) => data);
  }

  static createKanbanColumn(dto: CreateKanbanColumnDto) {
    return AtiraApi.post<void>(`/kanban/column/add`, dto).then(
      ({ data }) => data,
    );
  }

  static createAutomationRule(dto: CreateKanbanAutomationRuleDto) {
    return AtiraApi.post<KanbanAutomationRule>("/kanban/automation", dto).then(
      ({ data }) => data,
    );
  }

  static updateAutomationRule({
    ruleId,
    ...body
  }: UpdateKanbanAutomationRuleDto) {
    return AtiraApi.patch<KanbanAutomationRule>(
      `/kanban/automation/${ruleId}`,
      body,
    ).then(({ data }) => data);
  }

  static deleteAutomationRule({
    ruleId,
    ...body
  }: DeleteKanbanAutomationRuleDto) {
    return AtiraApi.delete<void>(`/kanban/automation/${ruleId}`, {
      data: body,
    }).then(({ data }) => data);
  }

  static assignKanbanTask(dto: AssignTaskDto) {
    return AtiraApi.post<void>(
      `/task/kanban/${dto.taskId}/manage-assignees`,
      dto,
    ).then(({ data }) => data);
  }

  static unAssignKanbanTask(dto: UnAssignTaskDto) {
    return AtiraApi.patch<void>(
      `/task/kanban/${dto.taskId}/unassign`,
      dto,
    ).then(({ data }) => data);
  }

  static getKanbanActivities(dto: GetKanbanActivitiesDto) {
    return AtiraApi.post<BaseActivity[]>(
      `/kanban/${dto.kanbanId}/activities`,
      dto,
    ).then(({ data }) => data);
  }

  static updateKanban(dto: UpdateKanbanDto) {
    return AtiraApi.patch<void>(`/kanban/${dto.kanbanId}`, dto).then(
      ({ data }) => data,
    );
  }

  static updateKanbanClosingReasons(dto: UpdateKanbanClosingReasonsDto) {
    return AtiraApi.patch<void>(
      `/kanban/${dto.kanbanId}/closing-reasons`,
      dto,
    ).then(({ data }) => data);
  }

  static updateKanbanCardBetweenKanbans(
    dto: UpdateKanbanCardBetweenKanbansDto,
  ) {
    return AtiraApi.patch<void>(
      `/kanban/card/${dto.cardId}/move-between-kanban`,
      dto,
    ).then(({ data }) => data);
  }

  static deleteKanbanCard(dto: DeleteKanbanCardDto) {
    return AtiraApi.delete<void>(`/kanban/card/${dto.cardId}`, {
      data: dto,
    }).then(({ data }) => data);
  }

  static deleteKanban(dto: DeleteKanbanDto) {
    return AtiraApi.delete<void>(`/kanban/${dto.kanbanId}`, { data: dto }).then(
      ({ data }) => data,
    );
  }

  static exportKanbanCards(dto: ExportKanbanCardsDto) {
    return AtiraApi.post<string>("/kanban/data/export-kanban-cards", dto).then(
      ({ data }) => data,
    );
  }
}
